import React from 'react';

import type { CampaignPerson } from './campaignOwner';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';

/**
 * The person requests act as right now — you, or whoever a superadmin is impersonating. Null until
 * `user.me` has loaded.
 */
export function useActingPerson(): CampaignPerson | null {
  const [auth] = useAuthContext();
  const userTeam = 'userTeam' in auth ? auth.userTeam : null;
  return React.useMemo(
    () =>
      userTeam == null
        ? null
        : {
            userId: userTeam.userId,
            name: userTeam.userTeamName,
            email: userTeam.userEmail,
            profilePicture: userTeam.profilePicture,
          },
    [userTeam],
  );
}

/**
 * The campaign's owner, from the same `cadenceTemplate.forEditor` query the campaign page makes, so
 * on the page it's a cache hit, not a request. Null while loading.
 */
export function useCampaignOwner(cadenceTemplateId: string | undefined): CampaignPerson | null {
  const campaign = api.cadenceTemplate.forEditor.useQuery(
    { cadenceTemplateId: cadenceTemplateId ?? '' },
    { enabled: cadenceTemplateId != null && cadenceTemplateId !== '' },
  );
  return campaign.data?.cadenceTemplate?.owner ?? null;
}

/**
 * The team's people as the guard names them, for putting names on sender counts. Fetched only while
 * something is about to show them (a bulk confirm), not on every page view.
 */
export function useTeamPeople(teamId: string, enabled: boolean): CampaignPerson[] {
  const query = api.team.teamUserOptions.useQuery({ teamId }, { enabled });
  return React.useMemo(
    () =>
      (query.data ?? []).map((u) => ({
        userId: u.value,
        name: u.name,
        email: u.email,
        profilePicture: u.profilePicture,
      })),
    [query.data],
  );
}
